import type { SiteLinkView, SiteNavNodeView } from '@/components/site-page/SitePage.types';
import { usePathname, useSearchParams } from '@/lib/app-router';

/** Where the viewer is: the path and the query string (no leading `?`). */
export interface NavLocation {
  pathname: string;
  search: string;
}

/** The current {@link NavLocation}; re-renders on a path OR a query change. */
export function useNavLocation(): NavLocation {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  return { pathname, search: searchParams?.toString() ?? '' };
}

/** A query string with its parameters in a stable order, so `a=1&b=2` equals `b=2&a=1`. */
function canonicalQuery(search: string): string {
  const params = new URLSearchParams(search);
  params.sort();
  return params.toString();
}

/**
 * Whether `link` is the viewer's current location.
 *
 * ## Invariants:
 * - Only an in-app link can be current. An external link never is.
 * - Path AND query must both match (fragment ignored), so `/reserve` and `/reserve?mode=monthly`
 *   (BOOK and MONTHLY) are never current together (PB-9032 review).
 */
export function isCurrentLink(link: SiteLinkView | null, location: NavLocation): boolean {
  if (!link || link.external) return false;
  const [pathAndQuery] = link.href.split('#');
  const [path, query = ''] = pathAndQuery.split('?');
  return path === location.pathname && canonicalQuery(query) === canonicalQuery(location.search);
}

export type CurrentMark = 'page' | 'ancestor' | false;

/**
 * Whether `node` is the current location (`page`), or a GROUP that contains it (`ancestor`).
 *
 * ## Invariants:
 * - Currency is {@link isCurrentLink}: path and query, in-app links only.
 * - A group is the ancestor when its Overview or any descendant link is the current location.
 */
export function currentMark(node: SiteNavNodeView, location: NavLocation): CurrentMark {
  if (node.kind !== 'GROUP') return isCurrentLink(node.link, location) ? 'page' : false;
  if (isCurrentLink(node.overview, location)) return 'ancestor';
  return node.children.some((child) => currentMark(child, location) !== false) ? 'ancestor' : false;
}
